import * as Accountant from 'app/calcs/Accountant'
import { FandfPensionCalculations } from 'app/calcs/customers/fandf'
import { FandfFormOfPayment, IFandfPensionInputs } from 'app/calcs/customers/fandf/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { NumberSelector } from 'app/components/form/NumberSelector'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo'
import { WorkbookServiceResponse } from 'app/services/models/Workbook'
import * as utils from 'app/utils'
import { RequestCache } from 'app/utils/RequestCache'
import { getBrandHelpers } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

interface IFandfPensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: string

  // these are synced with profile.pension.fandf
  beginPaymentsAge: moment.Duration
  formOfPayment: FandfFormOfPayment
  basePay: number
  annualSalaryIncrease: number
}

class FandfLocalPensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, IFandfPensionContainerState> {
  beginPaymentsAgeNS = 'pension.fandf.beginPaymentsAge'
  basePayNS = 'pension.fandf.basePay'
  formOfPaymentNS = 'pension.fandf.formOfPayment'
  annualSalaryIncreaseNS = 'pension.fandf.annualSalaryIncrease'

  state: IFandfPensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    basePay: this.getValueForQuestion(this.basePayNS) || 0,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
    annualSalaryIncrease: this.getValueForQuestion(this.annualSalaryIncreaseNS) || 3
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    // tslint:disable-next-line:no-console
    //console.log('fandf.debounceCalculations entered')
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.fandf
    const { beginPaymentsAge, formOfPayment } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = new FandfPensionCalculations(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
    .then((payout) => {
      const monthlyPayout = utils.toCurrency(payout.amount)
      this.setState({ monthlyPayout })
    })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.fandf) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: IFandfPensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateAnnualSalaryIncrease(annualSalaryIncrease: number) {
    this.setState({ annualSalaryIncrease })
    this.state.profile.pension.fandf.annualSalaryIncrease = annualSalaryIncrease
    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateCalculations(nextState: IFandfPensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, basePay, beginPaymentsAge } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment,
      basePay
    }
    const newFandf = _.defaults({}, updates, profile.pension.fandf)

    if (!this.initCalc || !_.isEqual(profile.pension.fandf, newFandf)) {
      this.initCalc = true
      profile.pension.fandf = newFandf
      this.debounceCalculations()
    }
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.fandf.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.fandf || !this.state.beginPaymentsAge) { return null }

    const censusDateText = theCensusInfo.censusDate.format('LL')

    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()
    const fandf = profile.pension.fandf

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = this.state.monthlyPayout !== '$0'

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter(getBrandHelpers().memberPensionFormsOfPayment, (e) => findFop(e))

    const aStyle = {
      marginBottom: '10px'
    }

    return (
      <section className="animated fadeIn">
        <h1>Foster &amp; Foster Pension</h1>

        <Row>
          <Col md={6}>

            <FormGroup>
              <NumberSelector width={'400px'} cellSuffix={'%'}
                placeholder="What average salary increase do you expect each year?"
                min={1} max={5} selectedValue={this.state.annualSalaryIncrease}
                fontSize="1.5vw"
                onChange={(a) => this.updateAnnualSalaryIncrease(a)} />
            </FormGroup>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={allowedFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>}

          </Col>
        </Row>
      </section >
    )
  }
}

const FandfPensionQuestionsGroup = () => (
  <FandfLocalPensionContainer
    header="Foster &amp; Foster Pension" icon="" />
)

export {
    FandfPensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/Fandf/index.tsx